import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import { Building2, Search, X, Download, PackageMinus, Plus, Trash2 } from "lucide-react";

// Generates a UTF-8 BOM CSV — opens cleanly in Excel including Arabic text.
const escapeCsv = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, filename) => {
  const csv = rows.map((r) => r.map(escapeCsv).join(",")).join("\n");
  // BOM is required for Excel to detect UTF-8 (esp. Arabic).
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function Inventory() {
  const { t, lang } = useLanguage();
  const { branches, activeBranchId } = useBranch();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("list");

  // Inventory is intrinsically per-branch now. We follow the active branch
  // from the global switcher and re-load whenever it changes.
  const load = async () => {
    const url = activeBranchId ? `/products?branch_id=${encodeURIComponent(activeBranchId)}` : "/products";
    const r = await api.get(url);
    setProducts(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeBranchId]);

  const currentBranch = branches.find((b) => b.branch_id === activeBranchId);
  const branchName = currentBranch ? (lang === "ar" ? (currentBranch.name_ar || currentBranch.name_en) : (currentBranch.name_en || currentBranch.name_ar)) : "";

  // Search filters by Arabic name + English name + SKU (case-insensitive).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      (p.name_en || "").toLowerCase().includes(q) ||
      (p.name_ar || "").toLowerCase().includes(q) ||
      (p.sku || "").toLowerCase().includes(q)
    );
  }, [products, search]);

  // KPIs always reflect the filtered view — so the user can see the value
  // of just the subset they're inspecting.
  const lowStock = filtered.filter((p) => p.stock <= (p.low_stock_threshold || 5));
  const totalValue = filtered.reduce((s, p) => s + p.stock * p.cost, 0);

  const exportExcel = () => {
    if (filtered.length === 0) {
      toast.error(lang === "ar" ? "لا توجد بيانات للتصدير" : "No data to export");
      return;
    }
    const headers = lang === "ar"
      ? ["الاسم", "SKU", "المخزون", "التكلفة", "السعر", "قيمة المخزون", "الفرع"]
      : ["Name", "SKU", "Stock", "Cost", "Price", "Stock value", "Branch"];
    const rows = [headers].concat(filtered.map((p) => {
      const name = lang === "ar"
        ? (p.name_ar || p.name_en || "")
        : (p.name_en || p.name_ar || "");
      return [
        name,
        p.sku || "",
        p.stock,
        Number(p.cost || 0).toFixed(2),
        Number(p.price || 0).toFixed(2),
        Number((p.stock || 0) * (p.cost || 0)).toFixed(2),
        branchName || "—",
      ];
    }));
    const datePart = new Date().toISOString().slice(0, 10);
    const safeBranch = (branchName || "all").replace(/\s+/g, "_");
    downloadCsv(rows, `inventory_${safeBranch}_${datePart}.csv`);
    toast.success(lang === "ar" ? `تم تصدير ${filtered.length} منتجاً` : `Exported ${filtered.length} products`);
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-4xl gold-text">{t("nav_inventory")}</h1>
        {branchName && (
          <div className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-[var(--gold-tint)] text-[var(--gold)] border border-[var(--gold)]/20">
            <Building2 size={14}/>
            <span>{lang === "ar" ? "تعرض مخزون: " : "Showing stock for: "}<strong>{branchName}</strong></span>
          </div>
        )}
      </div>

      {/* Tab strip — Inventory list / Internal issuance (صرف داخلي) */}
      <div className="border-b border-[var(--border)] flex gap-6">
        {[
          { id: "list", labelAr: "المنتجات", labelEn: "Products" },
          { id: "issue", labelAr: "صرف داخلي", labelEn: "Internal issuance" },
          { id: "history", labelAr: "سجل الصرف", labelEn: "Issuance log" },
        ].map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            data-testid={`inv-tab-${tb.id}`}
            className={`pb-3 text-sm font-bold border-b-2 transition ${tab === tb.id ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text)]"}`}
          >
            {lang === "ar" ? tb.labelAr : tb.labelEn}
          </button>
        ))}
      </div>

      {tab === "issue" && <InternalIssueTab products={products} reload={load} branchId={activeBranchId} lang={lang} />}
      {tab === "history" && <IssuanceHistoryTab lang={lang} />}
      {tab !== "list" ? null : (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="card-surface p-5">
          <div className="text-xs uppercase text-[var(--text-muted)]">{t("products_count")}</div>
          <div className="font-heading text-3xl gold-text mt-2">{filtered.length}</div>
        </div>
        <div className="card-surface p-5">
          <div className="text-xs uppercase text-[var(--text-muted)]">{lang === "ar" ? "إجمالي القطع" : "Total units"}</div>
          <div className="font-heading text-3xl text-[var(--text)] mt-2 num-display">{filtered.reduce((s, p) => s + (p.stock || 0), 0)}</div>
        </div>
        <div className="card-surface p-5">
          <div className="text-xs uppercase text-[var(--text-muted)]">{lang === "ar" ? "قيمة المخزون" : "Stock value"}</div>
          <div className="font-heading text-3xl text-[var(--text)] mt-2 num-display">{totalValue.toFixed(2)}</div>
        </div>
        <div className="card-surface p-5">
          <div className="text-xs uppercase text-[var(--text-muted)]">{t("low_stock")}</div>
          <div className="font-heading text-3xl rose-text mt-2">{lowStock.length}</div>
        </div>
      </div>

      {/* Search + Export bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={15} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none"/>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === "ar" ? "ابحث بالاسم أو SKU..." : "Search by name or SKU..."}
            className="input-dark ps-9 pe-8 py-2 w-full"
            data-testid="inventory-search"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="inventory-search-clear">
              <X size={14}/>
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-[var(--text-muted)]" data-testid="inventory-result-count">
            {search
              ? (lang === "ar" ? `${filtered.length} من ${products.length}` : `${filtered.length} of ${products.length}`)
              : `${products.length} ${lang === "ar" ? "منتج" : "products"}`}
          </div>
          <button
            onClick={exportExcel}
            disabled={filtered.length === 0}
            className="btn-gold px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
            data-testid="inventory-export-btn"
          >
            <Download size={14}/>{lang === "ar" ? "تصدير إلى Excel" : "Export to Excel"}
          </button>
        </div>
      </div>

      {/* Low-stock callout removed — the table now flags Out/Low rows clearly. */}

      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead>
            <tr>
              <th className="text-start">{t("name")}</th>
              <th className="w-36">{t("sku")}</th>
              <th className="w-24 text-center">{t("stock")}</th>
              <th className="w-24 text-end">{t("cost")}</th>
              <th className="w-24 text-end">{t("price")}</th>
              <th className="w-28 text-end">{lang === "ar" ? "قيمة المخزون" : "Stock value"}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const name = lang === "ar"
                ? (p.name_ar || p.name_en || "—")
                : (p.name_en || p.name_ar || "—");
              const threshold = p.low_stock_threshold || 5;
              const out = p.stock <= 0;
              const low = !out && p.stock <= threshold;
              const stockClass = out
                ? "text-rose-600 font-semibold"
                : low
                  ? "text-amber-600 font-semibold"
                  : "text-emerald-700 font-medium";
              return (
                <tr key={p.product_id} className="hover:bg-[var(--bg)] transition-colors" data-testid={`inventory-row-${p.product_id}`}>
                  <td className="text-[var(--text)]">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{name}</span>
                      {out && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold">{lang === "ar" ? "نفد" : "Out"}</span>}
                      {low && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">{lang === "ar" ? "منخفض" : "Low"}</span>}
                    </div>
                  </td>
                  <td>
                    <span className="font-mono text-xs text-[var(--text-muted)]">{p.sku || "—"}</span>
                  </td>
                  <td className={`text-center num-display ${stockClass}`}>{p.stock}</td>
                  <td className="text-end num-display text-[var(--text-muted)]">{Number(p.cost || 0).toFixed(2)}</td>
                  <td className="text-end num-display gold-text">{Number(p.price || 0).toFixed(2)}</td>
                  <td className="text-end num-display text-[var(--text)] font-medium">{Number((p.stock || 0) * (p.cost || 0)).toFixed(2)}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-[var(--text-muted)] py-12">{search ? (lang === "ar" ? "لا نتائج تطابق البحث" : "No results match your search") : t("no_data")}</td></tr>}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}

// ============================================================================
// Internal Issuance tab — draft a list of products to consume internally,
// then submit one combined "issue" that posts COGS to the GL on the backend.
// ============================================================================
function InternalIssueTab({ products, reload, branchId, lang }) {
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState({});           // { [product_id]: qty }
  const [reason, setReason] = useState("service_use");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isAr = lang === "ar";
  const norm = (s) => (s || "").toLowerCase();
  const fq = norm(search.trim());
  const visible = !fq ? products.slice(0, 12) : products.filter(p => norm(p.name_en).includes(fq) || norm(p.name_ar).includes(fq) || norm(p.sku).includes(fq));
  const lines = Object.entries(cart).map(([pid, qty]) => {
    const p = products.find(x => x.product_id === pid);
    if (!p) return null;
    const cost = Number(p.cost || 0);
    return { product_id: pid, name: isAr ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar), stock: p.stock, qty, cost, line: cost * qty };
  }).filter(Boolean);
  const totalCost = lines.reduce((s, l) => s + l.line, 0);

  const setQty = (pid, q) => {
    setCart((c) => {
      const next = { ...c };
      const n = parseInt(q || 0) || 0;
      if (n <= 0) delete next[pid]; else next[pid] = n;
      return next;
    });
  };

  const submit = async () => {
    if (lines.length === 0) { toast.error(isAr ? "أضف منتجات أولاً" : "Add products first"); return; }
    setSubmitting(true);
    try {
      const payload = {
        branch_id: branchId || undefined,
        reason,
        notes,
        items: lines.map(l => ({ product_id: l.product_id, qty: l.qty })),
      };
      const r = await api.post("/inventory/internal-issues", payload);
      toast.success(isAr ? `تم الصرف · تكلفة ${r.data?.total_cost?.toFixed?.(2) || 0}` : `Issued · cost ${r.data?.total_cost?.toFixed?.(2) || 0}`);
      setCart({}); setNotes("");
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "فشل الصرف" : "Issue failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-5" data-testid="internal-issue-tab">
      <div className="card-surface p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Search size={15} className="text-[var(--text-muted)]"/>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? "ابحث عن منتج..." : "Search product..."}
            data-testid="internal-issue-search"
            className="input-dark flex-1 py-2"
          />
        </div>
        <div className="text-xs text-[var(--text-muted)]">
          {isAr ? `${visible.length} من ${products.length} منتج · المتوسط = تكلفة شراء × الكمية` : `${visible.length} of ${products.length} products`}
        </div>
        <div className="max-h-[460px] overflow-y-auto -mx-1">
          {visible.map((p) => {
            const inCart = cart[p.product_id] || 0;
            return (
              <div key={p.product_id} className="flex items-center gap-2 p-2 hover:bg-[var(--bg)] rounded-md border-b border-[var(--border-subtle)]" data-testid={`internal-issue-product-${p.product_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[var(--text)] truncate">{isAr ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar)}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{p.sku} · {isAr ? "متاح" : "stock"}: {p.stock} · {isAr ? "تكلفة" : "cost"}: {Number(p.cost || 0).toFixed(2)}</div>
                </div>
                <input
                  type="number"
                  min="0"
                  max={p.stock}
                  value={inCart || ""}
                  onChange={(e) => setQty(p.product_id, e.target.value)}
                  data-testid={`internal-issue-qty-${p.product_id}`}
                  className="w-16 px-2 py-1 rounded bg-[var(--bg)] border border-[var(--border)] text-sm text-center num-display"
                  placeholder="0"
                />
              </div>
            );
          })}
          {visible.length === 0 && <div className="text-center text-[var(--text-muted)] py-8 text-sm">{isAr ? "لا منتجات" : "No products"}</div>}
        </div>
      </div>

      <div className="card-surface p-4 space-y-3 lg:sticky lg:top-4 self-start" data-testid="internal-issue-summary">
        <div className="text-sm font-bold flex items-center gap-2 text-[var(--gold)]"><PackageMinus size={15}/>{isAr ? "ملخص الصرف" : "Issue summary"}</div>
        {lines.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)]">{isAr ? "أدخل الكميات لعرض الإجمالي." : "Enter quantities to see the total."}</p>
        ) : (
          <ul className="space-y-1 text-xs max-h-[200px] overflow-y-auto">
            {lines.map(l => (
              <li key={l.product_id} className="flex items-center justify-between border-b border-[var(--border-subtle)] pb-1.5" data-testid={`issue-line-${l.product_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[var(--text)] truncate">{l.name}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{l.qty} × {l.cost.toFixed(2)}</div>
                </div>
                <div className="num-display font-bold text-[var(--gold)]">{l.line.toFixed(2)}</div>
                <button onClick={() => setQty(l.product_id, 0)} className="ms-1 p-1 rounded hover:bg-rose-500/10"><Trash2 size={12} className="text-rose-500"/></button>
              </li>
            ))}
          </ul>
        )}
        <div className="pt-2 border-t border-[var(--border)] flex items-center justify-between text-sm">
          <span className="text-[var(--text-muted)]">{isAr ? "إجمالي التكلفة" : "Total cost"}</span>
          <span className="font-bold text-lg num-display text-[var(--gold)]" data-testid="internal-issue-total">{totalCost.toFixed(2)}</span>
        </div>
        <div className="text-[10px] text-[var(--text-muted)] leading-relaxed">
          {isAr ? "سيُرحَّل هذا المبلغ على حساب \"تكلفة الخدمات\" (5110) خصماً من المخزون (1140)." : "Will be debited to \"Cost of Services\" (5110) and credited to Inventory (1140)."}
        </div>
        <div className="space-y-2">
          <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-bold">{isAr ? "السبب" : "Reason"}</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="internal-issue-reason" className="input-dark w-full py-2 text-sm">
            <option value="service_use">{isAr ? "استخدام في الخدمة" : "Service use"}</option>
            <option value="training">{isAr ? "تدريب" : "Training"}</option>
            <option value="damage_in_use">{isAr ? "تالف أثناء الاستخدام" : "Damaged in use"}</option>
            <option value="sample">{isAr ? "عينة مجانية" : "Free sample"}</option>
            <option value="other">{isAr ? "أخرى" : "Other"}</option>
          </select>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={isAr ? "ملاحظات" : "Notes"} rows={2} data-testid="internal-issue-notes" className="input-dark w-full text-xs"/>
        </div>
        <button onClick={submit} disabled={submitting || lines.length === 0} data-testid="internal-issue-submit" className="btn-gold w-full py-2.5 rounded-md font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Plus size={14}/>{submitting ? (isAr ? "جاري..." : "Issuing...") : (isAr ? "صرف الآن" : "Issue now")}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Issuance history — recent internal-issue ledger entries.
// ============================================================================
function IssuanceHistoryTab({ lang }) {
  const [items, setItems] = useState([]);
  const isAr = lang === "ar";
  useEffect(() => {
    api.get("/inventory/internal-issues").then(r => setItems(r.data || [])).catch(() => setItems([]));
  }, []);
  return (
    <div className="card-surface" data-testid="issuance-history-tab">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-[var(--text-muted)] text-xs uppercase tracking-wider">
          <tr>
            <th className="p-3 text-start">{isAr ? "التاريخ" : "Date"}</th>
            <th className="p-3 text-start">{isAr ? "السبب" : "Reason"}</th>
            <th className="p-3 text-start">{isAr ? "عدد الأصناف" : "Items"}</th>
            <th className="p-3 text-end">{isAr ? "إجمالي التكلفة" : "Total cost"}</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[var(--text-muted)]">{isAr ? "لا توجد عمليات صرف بعد" : "No issuance records yet"}</td></tr>}
          {items.map(it => (
            <tr key={it.issue_id} data-testid={`issuance-row-${it.issue_id}`} className="border-t border-[var(--border-subtle)]">
              <td className="p-3 text-[var(--text-muted)]">{(it.created_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="p-3"><span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--gold)]/10 text-[var(--gold)] border border-[var(--gold)]/20">{it.reason}</span></td>
              <td className="p-3 num-display">{(it.items || []).length}</td>
              <td className="p-3 text-end num-display font-bold text-[var(--gold)]">{Number(it.total_cost || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
